import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled } from '../lib/reportPrefill';
import { defaultReportDate, maxReportDate } from '../lib/reportDates';
import ReportingCurrencyField from './ReportingCurrencyField';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Security Cross Reference - Custom Popup
 * 
 * This report displays a selected security's quantity, cost basis, market value,
 * and unrealized gain or loss for each portfolio as of a selected date.
 * 
 * Based on PDF screenshot fields:
 * - Portfolio (with @trading syntax support)
 * - Group Name
 * - Date
 * - Reporting Currency
 * - Security Type
 * - Security Symbol (CUSIP)
 */
const SecurityCrossReferencePopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [asOfDate, setAsOfDate] = useState('');
  const [currency] = useState('USD');
  const [securityType, setSecurityType] = useState('');
  const [securitySymbol, setSecuritySymbol] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Security type options - values are live APX sectypecode values from the
  // lake (mb/cb/tb/us/cs/mm/mf), not descriptive labels. The backend filters
  // on LOWER(app.sectypecode) = LOWER(value), so any value not in that set
  // returns zero rows.
  const securityTypes = [
    { value: '', label: 'All Types' },
    { value: 'mb', label: 'Municipal Bonds' },
    { value: 'cb', label: 'Corporate Bonds' },
    { value: 'tb', label: 'Treasury Bills' },
    { value: 'us', label: 'US Government Bonds' },
    { value: 'cs', label: 'Common Stock' },
    { value: 'mm', label: 'Money Market' },
    { value: 'mf', label: 'Mutual Funds' },
  ];

  useEffect(() => {
    if (isOpen) {
      // Default to yesterday (latest as-of date with Pershing data)
      setAsOfDate(prefilled(initialParams, 'date', defaultReportDate()));
      setSecuritySymbol(prefilled(initialParams, 'security_symbol', ''));
      setSecurityType(prefilled(initialParams, 'security_type', ''));
    }
  }, [isOpen, initialParams]);

  // The single thin-params builder used by handleSubmit.
  const buildParams = (sel) => ({
    date: asOfDate,
    currency: currency,
    security_symbol: securitySymbol.toUpperCase(),
    security_type: securityType || undefined,
    // Firm-wide is the DEFAULT: this report exists to show who holds the
    // security, so an empty picker scopes to every account. Picking a
    // group/account narrows it. all_accounts is the typed backend flag;
    // client_name '@@ALL' is the sentinel that survives
    // buildReportRequestParams (which does not forward the raw boolean).
    ...(sel.selectedItem
      ? sel.params
      : { all_accounts: true, client_name: '@@ALL' }),
  });

  const validateForm = () => {
    const newErrors = {};

    // No portfolio requirement: this report answers "who holds this security",
    // so an empty picker means firm-wide (the default), not a missing field.

    if (!securitySymbol.trim()) {
      newErrors.securitySymbol = 'Security Symbol (CUSIP) is required';
    }

    if (!asOfDate) {
      newErrors.asOfDate = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await onConfirm(buildParams(sel));

      // Reset form
      setSecuritySymbol('');
      setSecurityType('');
      setSelection({ selectedItem: null, params: {} });
      setErrors({});
    } catch (error) {
      console.error('Error generating report:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSecuritySymbol('');
    setSecurityType('');
    setSelection({ selectedItem: null, params: {} });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg mx-4 rounded-lg shadow-xl ${
        darkMode ? 'bg-[#141414]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <div>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Security Cross Reference - Custom
            </h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              This report displays a selected security's quantity, cost basis, market value, and unrealized gain or loss for each portfolio as of a selected date.
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`p-1 rounded-full hover:bg-gray-200 ${darkMode ? 'hover:bg-[#1a1a1a]' : ''}`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Portfolio Field (shared selector: groups vs accounts, @@ aggregate).
              Optional here - empty means firm-wide, this report's default. */}
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleSubmit}  disabled={isLoading}/>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Leave empty to cross-reference every account firm-wide.
            </p>
          </div>

          {/* Date */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={asOfDate}
              max={maxReportDate()}
              onChange={(e) => setAsOfDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.asOfDate ? 'border-red-500' : ''
              } ${
                darkMode 
                  ? 'bg-[#1a1a1a] border-white/10 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}/>
            {errors.asOfDate && (
              <p className="text-red-500 text-sm mt-1">{errors.asOfDate}</p>
            )}
          </div>

          {/* Reporting Currency */}
          <ReportingCurrencyField darkMode={darkMode} />

          {/* Security Type */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Security Type
            </label>
            <select
              value={securityType}
              onChange={(e) => setSecurityType(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-[#1a1a1a] border-white/10 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}>
              {securityTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Security Symbol (CUSIP) */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Security Symbol (CUSIP) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={securitySymbol}
              onChange={(e) => setSecuritySymbol(e.target.value.toUpperCase())}
              placeholder="e.g., 64972ETE9"
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.securitySymbol ? 'border-red-500' : ''
              } ${
                darkMode 
                  ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}/>
            {errors.securitySymbol && (
              <p className="text-red-500 text-sm mt-1">{errors.securitySymbol}</p>
            )}
          </div>

          {/* Error message */}
          {errors.submit && (
            <div className="p-3 rounded-lg bg-red-100 text-red-700 text-sm">
              {errors.submit}
            </div>
          )}
        </div>

        {/* Footer with buttons matching PDF (OK, Cancel) */}
        <div className={`flex items-center justify-end gap-2 p-4 border-t ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300' : ''}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className={darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
          >
            {isLoading ? 'Generating...' : 'OK'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecurityCrossReferencePopup;

